<template>
    <div class="app-i18n-scope">
        <TableNameLabel />
        <TableAllocationInfoButton />
        <template v-if="isServicesViewVisible">
            <Teleport to=".view.services">
                <ServicesView />
            </Teleport>
        </template>
        <template v-if="isOpenBillsViewVisible">
            <Teleport to=".view.open-bills .open-bills-list">
                <OpenBillsView />
            </Teleport>
        </template>
        <template v-if="isPreorderTimeViewVisible">
            <Teleport to=".view.preorder-time">
                <PreorderTimeView />
            </Teleport>
        </template>
        <template v-if="isRoomPlanViewVisible">
            <Teleport to=".view.room-plan">
                <RoomPlanView :transferBillOptions="roomPlanViewData?.transferBillOptions" />
            </Teleport>
        </template>
        <template v-if="isPaymentMethodsReportVisible">
            <Teleport to=".view.payment-methods-report">
                <PaymentMethodsReportView />
            </Teleport>
        </template>
        <template v-if="isOrderViewVisible">
            <Teleport to=".view.order">
                <OrderView />
            </Teleport>
        </template>
        <template v-if="isCartViewVisible">
            <Teleport to=".view.cart">
                <CartView />
            </Teleport>
        </template>
        <template v-if="isBillViewVisible">
            <Teleport to=".view.bill">
                <BillView />
            </Teleport>
        </template>
        <template v-if="isReceiptViewVisible">
            <Teleport to=".view.receipt">
                <ReceiptView :data="receiptViewData!" />
            </Teleport>
        </template>
        <template v-if="isPaymentReceiptViewVisible">
            <Teleport to=".view.payment-receipt">
                <PaymentReceiptView :data="paymentReceiptViewData!" />
            </Teleport>
        </template>
        <CartItemCommentEditorsContainer v-if="!features.vueCartController" />
        <KioskModeConfiguration />
        <PrimevueConfig />
    </div>
</template>

<script setup lang="ts">
    import { features } from "@/app/App";
    import { TastierClient } from "@/app/TastierClient";
    import { BillController_new } from "@/app/controller/BillController_new";
    import { CartController } from "@/app/controller/CartController";
    import { OpenBillsController } from "@/app/controller/OpenBillsController";
    import { OrderController } from "@/app/controller/OrderController";
    import { PaymentMethodReportController } from "@/app/controller/PaymentMethodReportController";
    import { PaymentReceiptController } from "@/app/controller/PaymentReceiptController";
    import { PreorderTimeController } from "@/app/controller/PreorderTimeController";
    import { ReceiptController } from "@/app/controller/ReceiptController";
    import { RoomPlanController } from "@/app/controller/RoomPlanController";
    import { ServicesControllerVue } from "@/app/controller/ServicesControllerVue";
    import { appEvents } from "@/app/events/appEvents";
    import { globalStores } from "@/app/globalStores";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { Helper } from "@/app/ui/Helper";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { ICustomerOrder } from "@/data/model/Customer";
    import type { ICustomerPayment } from "@/data/model/CustomerPayment";
    import type { ShowRoomPlanEventData } from "@/data/model/Table";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { usePaymentStore } from "@/data/stores/payment";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { logError } from "@/shared/logging/errors";
    import { onBeforeMount, ref } from "vue";

    import PrimevueConfig from "./components/PrimevueConfig.vue";
    import TableAllocationInfoButton from "@/app/components/TableAllocationInfoButton.vue";
    import TableNameLabel from "@/app/components/TableNameLabel.vue";
    import CartItemCommentEditorsContainer from "@/app/components/cart/CartItemCommentEditorsContainer.vue";
    import KioskModeConfiguration from "@/app/components/kiosk/KioskModeConfiguration.vue";
    import BillView from "@/app/views/BillView.vue";
    import CartView from "@/app/views/CartView.vue";
    import OpenBillsView from "@/app/views/OpenBillsView.vue";
    import OrderView from "@/app/views/OrderView.vue";
    import PaymentMethodsReportView from "@/app/views/PaymentMethodsReportView.vue";
    import PaymentReceiptView from "@/app/views/PaymentReceiptView.vue";
    import PreorderTimeView from "@/app/views/PreorderTimeView.vue";
    import ReceiptView from "@/app/views/ReceiptView.vue";
    import RoomPlanView from "@/app/views/RoomPlanView.vue";
    import ServicesView from "@/app/views/ServicesView.vue";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";
    import { useOrderedArticlesStore } from "@/data/stores/orderedArticles";

    const identity = useIdentityStore();
    const allocation = useTableAllocationStore();
    const bill = useBillStore();
    const offer = useOfferStore();
    const cart = useCartStore();
    const payment = usePaymentStore();
    const kiosk = useKioskStore();
    const articleIconAttributes = useArticleAttributesStore();
    const orderedArticles = useOrderedArticlesStore();

    const isServicesViewVisible = ref(false);
    const isOpenBillsViewVisible = ref(false);
    const isPreorderTimeViewVisible = ref(false);
    const isRoomPlanViewVisible = ref(false);
    const isPaymentMethodsReportVisible = ref(false);
    const isOrderViewVisible = ref(false);
    const isCartViewVisible = ref(false);
    const isBillViewVisible = ref(false);
    const isReceiptViewVisible = ref(false);
    const isPaymentReceiptViewVisible = ref(false);

    const receiptViewData = ref<ICustomerOrder | undefined>();
    const paymentReceiptViewData = ref<ICustomerPayment | undefined>();
    const roomPlanViewData = ref<ShowRoomPlanEventData>();

    onBeforeMount(() => {
        globalStores.billStore = bill;
        globalStores.cartStore = cart;
        globalStores.identityStore = identity;
        globalStores.offerStore = offer;
        globalStores.paymentStore = payment;
        globalStores.tableAllocationStore = allocation;
        globalStores.kioskStore = kiosk;
        globalStores.articleAttributeStore = articleIconAttributes;
        globalStores.orderedArticlesStore = orderedArticles;

        appEvents.appContext.AllocationChanged.add((eventData) => {
            allocation.setCurrentAllocation(eventData.currentAllocation ?? null);
            try {
                if (identity.hasOperatorAccess) {
                    allocation.refreshGuestsInfo();
                }
            } catch (ex) {
                logError(ex);
            }
        });

        appEvents.appContext.ItemSelectionChanged.add((selections) => {
            bill.updateSelectedItems(selections);
        });

        appEvents.appContext.BillChanged.add((eventData) => {
            bill.setCurrentBill(eventData.currentBill === undefined ? null : eventData.currentBill);
        });

        appEvents.appContext.OfferLoadingStarted.add(() => {
            offer.setIsLoading(true);
        });

        appEvents.appContext.OfferChanged.add(() => {
            offer.setCurrentOffer(TastierClient.context.currentOffer ?? null);
            offer.setIsLoading(false);
        });

        appEvents.appContext.SelectedServiceChanged.add(async () => {
            const orderService = Helper.getCurrentOrderOrOfferService();
            await offer.setOrderService(orderService ?? null, false);

            const payService = Helper.getCurrentPayService();
            await payment.setPaymentService(payService ?? null);
        });

        appEvents.identity.identityChanged.add((eventData) => {
            identity.onIdentityChanged(eventData);
        });

        dataEvents.appSocket.TableCovidUserCountChanged.add(async (ev) => {
            if (allocation.tableId != ev.tableId) return;

            await allocation.refreshGuestsInfo();
        });

        dataEvents.appSocket.ProfileChanged.add(async (ev) => {
            if (cart.currentCart?.contactDataEndUser?.identityId != ev.identityId) {
                console.log("Cart contains diff indirect identity, to skip");
            } else {
                console.log("Load cart to refresh contact data");
                await cart.loadCurrentCart();
            }
        });

        dataEvents.appSocket.BillManualEndUsersCountChanged.add(async (ev) => {
            if (bill.currentBill?.id != ev.billId) return;

            bill.updateBillManualEndUsersCount(ev.billId, ev.manualEndUsersCount);
        });

        dataEvents.appSocket.BillOpened.add(orderedArticles.onBillEvent);
        dataEvents.appSocket.BillChangedShared.add(orderedArticles.onBillEvent);
        dataEvents.appSocket.BillClosed.add(orderedArticles.onBillEvent);

        dataEvents.appSocket.CartChanged.add((eventData) => {
            cart.setCurrentCart(eventData.cart ?? null);
        });

        dataEvents.appSocket.CartDiscountChanged.add((eventData) => {
            cart.setCurrentCart(eventData.cart ?? null);
        });

        ServicesControllerVue.onInit.add(() => {
            isServicesViewVisible.value = true;
        });

        OpenBillsController.onShown.add(() => {
            isOpenBillsViewVisible.value = true;
        });

        OpenBillsController.onHidden.add(() => {
            isOpenBillsViewVisible.value = false;
        });

        PreorderTimeController.onInit.add(() => {
            isPreorderTimeViewVisible.value = true;
        });

        RoomPlanController.onShown.add(async (ev) => {
            roomPlanViewData.value = ev;
            isRoomPlanViewVisible.value = true;
        });

        RoomPlanController.onHidden.add(() => {
            roomPlanViewData.value = undefined;
            isRoomPlanViewVisible.value = false;
        });

        PaymentMethodReportController.onInit.add(() => {
            isPaymentMethodsReportVisible.value = true;
        });

        OrderController.onInit.add(() => {
            isOrderViewVisible.value = true;
        });

        CartController.onInit.add(() => {
            isCartViewVisible.value = true;
        });

        BillController_new.onInit.add(() => {
            isBillViewVisible.value = true;
        });

        ReceiptController.onShown.add(async (data) => {
            receiptViewData.value = data;
            isReceiptViewVisible.value = true;
        });

        ReceiptController.onHidden.add(async () => {
            setTimeout(() => {
                receiptViewData.value = undefined;
                isReceiptViewVisible.value = false;
            }, 200);
        });

        PaymentReceiptController.onShown.add(async (data) => {
            paymentReceiptViewData.value = data;
            isPaymentReceiptViewVisible.value = true;
        });

        PaymentReceiptController.onHidden.add(async () => {
            setTimeout(() => {
                paymentReceiptViewData.value = undefined;
                isPaymentReceiptViewVisible.value = false;
            }, 200);
        });
    });
</script>

<style lang="scss">
    @use "./../../assets/styles/scss/shared/_colors.scss";

    #tastierapp {
        background-color: #fff;
        max-width: 640px;
        width: 100%;
        margin: 0 auto;
        height: 100dvh;
        position: relative;
        border-left: 1px solid #ccc;
        border-right: 1px solid #ccc;

        /* the overflow stays with .app in app/_pwa.scss: hidden on both axes. every child of
          this element is absolutely positioned inside its box, so there is nothing here that
          could ever need to scroll - the only thing a scrollable y axis ever exposed was the
          closed popover, which waits below the bottom edge on a translate and gave the whole
          app a scroll bar for as long as it was in the dom
        */

        font-weight: 300;

        /* FIX 2: Switch from 'auto' to 'touch' for smooth momentum scrolling
          on mobile browsers (iOS/Android WebView).
        */
        -webkit-overflow-scrolling: touch;

        overscroll-behavior: none;

        .v-btn {
            transition-duration: 0.2s;

            &.primary {
                background-color: var(--colorPrimary);
                border-color: var(--colorPrimary);

                &:hover,
                &:active {
                    background-color: var(--colorPrimaryHover);
                    border-color: var(--colorPrimaryHover);
                }
            }

            &.warning {
                background-color: colors.$orange;
                border-color: colors.$orange;

                &:hover,
                &:active {
                    background-color: colors.$orangeHover;
                    border-color: colors.$orangeHover;
                }
            }
        }
    }
</style>
